import { useEffect, useMemo, useState } from "react";
import { Listbox, ListboxItem } from "@heroui/listbox";
import { Avatar } from "@heroui/avatar";
import { Input } from "@heroui/input";
import { Skeleton } from "@heroui/skeleton";
import { useSession } from "@clerk/clerk-react";
import { CirclePlus, EllipsisVertical, User, CalendarPlus, History, Trash2 } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter } from "@heroui/drawer";
import { Button } from "@heroui/button";
import { addToast } from "@heroui/toast";
import { getMyConnections as getMyConnectionsApi } from "@/api/users";
import CreateConnectionDrawer from "../drawers/create-connection-drawer";
import PulledDownWrapperV2 from "../wrappers/pulled-down-wrapper-2";
import { removeMyConnection as removeMyConnectionApi } from "@/api/users";

const MyConnectionsComp = ({ frameless = false, maxHeight = "70vh" }) => {
const { session } = useSession();

const [connections, setConnections] = useState([]);
const [search, setSearch] = useState("");
const [loading, setLoading] = useState(true);
const [fetchError, setFetchError] = useState("");
const [showCreate, setShowCreate] = useState(false);

const [showActions, setShowActions] = useState(false);
const [selectedUser, setSelectedUser] = useState(null);

useEffect(() => {
  getMyConnections();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

async function getMyConnections() {
  setLoading(true);
  setFetchError("");
  try {
    const token = await session?.getToken();
    const _resp = await getMyConnectionsApi(token);
    if (_resp.data.Result === "OK") {
      setConnections(_resp.data.Records || []);
    } else {
      setFetchError("Failed to load connections");
    }
  } catch (err) {
    console.log(err);
    setFetchError("Failed to load connections");
  } finally {
    setLoading(false);
  }
}

function onCreateMemberDrawerResp(cmd) {
  if (cmd === "close") {
    setShowCreate(false);
    getMyConnections(); // refresh
  }
}

const filteredUsers = useMemo(() => {
  if (loading) return [];
  const q = search.trim().toLowerCase();
  if (!q) return connections;
  return connections.filter((u) => {
    const name = u.fullname?.toLowerCase() || "";
    const email = u.email_address?.toLowerCase() || "";
    return name.includes(q) || email.includes(q);
  });
}, [connections, search, loading]);

const handleAction = (key, user) => {
  if(key === 'delete_connection'){
    onRemoveConnection(user)
  }
  setShowActions(false)
};

async function onRemoveConnection(_connection){
  const token = await session?.getToken();
  removeMyConnectionApi(_connection['id'], token).then((_resp) => {
    if(_resp.data.Result === 'SUCCESS'){
      getMyConnections()
      addToast({
        title: "Connection Removed",
        color: 'success',
      })
    }
  }).catch((_resp) => {
    console.error(_resp)
  }).finally(() => {
    
  })
}

// Chrome: if frameless, let parent card own the background.
const shellClass = frameless
  ? "overflow-hidden"
  : "rounded-xl shadow-sm bg-white dark:bg-content1 border border-neutral-200 dark:border-default-200/60 overflow-hidden";

// Sticky header must match parent background EXACTLY
const stickyHeaderClass =
  "sticky top-0 z-10 bg-inherit border-b border-neutral-200 dark:border-default-100/40 p-2";

return (
  <PulledDownWrapperV2 onRefresh={getMyConnections}>
    <div className="w-full flex flex-col gap-2">
      <div className={shellClass}>
        {/* Sticky search bar — inherits parent bg (no opacity/blur) */}
        <div className={stickyHeaderClass}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            placeholder="Search connections..."
            className="flex-1"
            classNames={{
              inputWrapper:
                "bg-transparent hover:bg-transparent border border-neutral-200 rounded-xl " +
                "dark:bg-transparent dark:border-default-200/60",
              input: "text-neutral-900 placeholder:text-neutral-400 dark:text-foreground",
            }}
            endContent={
              <button
                onClick={() => setShowCreate(true)}
                type="button"
                className="text-neutral-500 hover:text-neutral-700 dark:text-foreground"
                aria-label="Create new contact"
                title="Create new contact"
              >
                <CirclePlus className="w-5 h-5" />
              </button>
            }
          />
        </div>

        {/* Scrollable body — also inherit background */}
        <div className="overflow-auto bg-inherit" style={{ maxHeight }}>
          {loading ? (
            <div className="p-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-3 py-3 border-b last:border-b-0 border-neutral-100 dark:border-default-100/30"
                >
                  <Skeleton className="rounded-full w-8 h-8" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-40 mb-2" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {fetchError && (
                <div className="text-red-500 text-sm px-3 py-3">{fetchError}</div>
              )}

              {!fetchError && filteredUsers.length === 0 ? (
                <div className="text-neutral-500 dark:text-default-500 text-sm px-3 py-6 text-center">
                  No connections found
                </div>
              ) : (
                <Listbox
                  aria-label="My connections"
                  items={filteredUsers}
                  selectionMode="none"
                  classNames={{
                    base: "bg-transparent shadow-none", // inside parent card
                    list: "max-h-none", // scrolling handled by outer div
                  }}
                >
                  {(item) => (
                    <ListboxItem
                      key={String(item.id)}
                      textValue={item.fullname || String(item.id)}
                      className="p-0 hover:bg-neutral-50 dark:hover:bg-default-100/60"
                      isReadOnly
                    >
                      <div className="px-3 py-3 border-b last:border-b-0 border-neutral-100 dark:border-default-100/30">
                        <div className="flex items-center gap-3">
                          <Avatar
                            size="sm"
                            src={`https://api.dicebear.com/9.x/thumbs/svg?seed=${encodeURIComponent(
                              item.fullname || String(item.id)
                            )}`}
                          />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-sm font-medium text-neutral-800 dark:text-foreground capitalize truncate">
                              {item.fullname || "—"}
                            </span>
                            <span className="text-xs text-neutral-600 dark:text-default-500 truncate">
                              {item.email_address || "—"}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedUser(item);
                              setShowActions(true);
                            }}
                            className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-default-100/60"
                            aria-label="Actions"
                            title="Actions"
                          >
                            <EllipsisVertical className="w-5 h-5 text-neutral-500 dark:text-default-500" />
                          </button>
                        </div>
                      </div>
                    </ListboxItem>
                  )}
                </Listbox>
              )}
            </>
          )}
        </div>
      </div>

      <CreateConnectionDrawer show={showCreate} onResp={onCreateMemberDrawerResp} />

      <Drawer
        isOpen={showActions}
        onOpenChange={setShowActions}
        placement="bottom"
        size="sm"
        shouldBlockScroll
        hideCloseButton={false}
        classNames={{ backdrop: "bg-black/30" }}
        backdrop={"blur"}
      >
        <DrawerContent>
          <DrawerHeader className="flex flex-col items-start">
            <div className="text-base font-semibold capitalize">
              {selectedUser?.fullname || "Actions"}
            </div>
            <div className="text-xs text-default-500">{selectedUser?.email_address}</div>
          </DrawerHeader>

          <DrawerBody className="flex flex-col gap-2">
            <Button
              variant="flat"
              className="justify-start gap-2"
              onPress={() => handleAction("view_profile", selectedUser)}
              startContent={<User className="w-4 h-4" />}
            >
              View Profile
            </Button>
            <Button
              variant="flat"
              className="justify-start gap-2"
              onPress={() => handleAction("create_meet", selectedUser)}
              startContent={<CalendarPlus className="w-4 h-4" />}
            >
              Create Meets
            </Button>
            <Button
              variant="flat"
              className="justify-start gap-2"
              onPress={() => handleAction("past_meets", selectedUser)}
              startContent={<History className="w-4 h-4" />}
            >
              Past Meets
            </Button>

            <div className="border-t border-default-200 my-2" />

            <Button
              color="danger"
              variant="flat"
              className="justify-start gap-2"
              onPress={() => handleAction("delete_connection", selectedUser)}
              startContent={<Trash2 className="w-4 h-4" />}
            >
              Delete Connection
            </Button>
          </DrawerBody>

          <DrawerFooter />
        </DrawerContent>
      </Drawer>
    </div>
  </PulledDownWrapperV2>
);
};

export default MyConnectionsComp;